// src/components/TeamStats.js
import React, { useState, useEffect } from 'react';
import { firestore } from '../firebase';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { calculateBadges, computeRawStats, ALL_BADGES } from './BadgeEngine';
import './TeamStats.css';

const EMPTY_FRAMES = Array.from({ length: 10 }, () => ({ roll1: '', roll2: '', roll3: '' }));

export default function TeamStats() {
  const [gamesByUser, setGamesByUser] = useState({});
  const [userProfiles, setUserProfiles] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAllGames = async () => {
      setLoading(true);
      try {
        const snapshot = await getDocs(query(collection(firestore, 'games'), orderBy('createdAt', 'desc')));
        const grouped = {};
        const profiles = {};
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          const userId = data.userId || 'unknown';
          if (!profiles[userId]) {
            profiles[userId] = { 
              displayName: data.UserName || data.userName || userId, 
              photoURL: data.photoURL || '' 
            };
          }
          if (!grouped[userId]) grouped[userId] = [];
          grouped[userId].push({ id: doc.id, ...data, frames: data.frames || EMPTY_FRAMES });
        });
        setGamesByUser(grouped);
        setUserProfiles(profiles);
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    loadAllGames();
  }, []);

  if (loading) return <div className="loading">Syncing League Power Rankings...</div>;

  return (
    <div className="team-stats-container">
      <h1 className="neon-title">League Power Rankings</h1>
      <div className="team-grid">
        {Object.keys(gamesByUser).map((userId) => {
          const games = gamesByUser[userId];
          const profile = userProfiles[userId] || {};
          const stats = computeRawStats(games);
          const badges = calculateBadges(games);

          return (
            <div key={userId} className="player-card">
              <div className="player-header">
                <img src={profile.photoURL} className="player-avatar" alt="" />
                <div className="player-info">
                  <h2 className="player-name">{profile.displayName}</h2>
                  <div className="card-badges">
                    {badges.map(bId => {
                      const b = ALL_BADGES.find(x => x.id === bId);
                      return <span key={bId} title={b?.label}>{b?.icon}</span>;
                    })}
                  </div>
                </div>
              </div>

              <div className="player-stats-grid">
                <div className="p-stat">
                  <span className="p-label">AVG</span>
                  <span className="p-value">{(stats.totalScore / games.length).toFixed(1)}</span>
                </div>
                <div className="p-stat">
                  <span className="p-label">SPARE %</span>
                  <span className="p-value">{stats.sparePct}%</span>
                </div>
                <div className="p-stat">
                  <span className="p-label">GAMES</span>
                  <span className="p-value">{games.length}</span>
                </div>
              </div>

              <div className="detailed-metrics-list">
                <div className="metric-item"><span>Strike %</span><strong>{stats.strikePct}%</strong></div>
                <div className="metric-item"><span>Single Pin</span><strong>{stats.singlePinPct}%</strong></div>
                <div className="metric-item"><span>Multi Pin</span><strong>{stats.multiPinPct}%</strong></div>
                
                <div className="metric-item highlight-link">
                  <span>Strike Streak</span>
                  <a href={`/game/${stats.bestStrikeGameId}`}>
                    <strong>{stats.maxStrikeStreak} 🔥</strong>
                  </a>
                </div>
                
                <div className="metric-item highlight-link">
                  <span>Spare Streak</span>
                  <a href={`/game/${stats.bestSpareGameId}`}>
                    <strong>{stats.maxSpareStreak} 🛡️</strong>
                  </a>
                </div>
              </div>

              <div className="recent-games-mini">
                <div className="score-pills">
                  {games.slice(0, 5).map((g, idx) => (
                    <div key={idx} className="score-pill">{g.totalScore}</div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}